// 箱单发票 단가·중량 계산 — 순수 함수(import 없음, 단위 테스트: __tests__/invoicePack.test.mjs)
//
// 합포장 申报单价(2026-10-08 YZY): 작업요청 칸에 「10P打包」처럼 N개 묶음이 적혀 있으면
// 출고리스트의 가격USD 는 낱개 단가라서 申报단가 = 낱개 단가 × N. 한 포장에 속성이 여럿(「3种属性合并打包」 = 같은 박스·바코드의
// 「/」 구성품 행)이면 포장 안 각 상품(대표행 + 구성품행)의 단가 × N 을 모두 더한다.
// 「P打包」가 없고 구성품도 없는 일반 건은 가격USD 그대로다.

const txt = v => (v === null || v === undefined ? '' : String(v).replace(/\r/g, '').trim())
function numOf(v) {
  if (typeof v === 'number' && isFinite(v)) return v
  const n = parseFloat(txt(v).replace(/[$,\s]/g, ''))
  return isFinite(n) ? n : 0
}
const round6 = v => Math.round(v * 1e6) / 1e6

// 「10P打包」「2P 打包」 → 10, 없으면 1
export function packCount(requestText) {
  const m = txt(requestText).match(/(\d+)\s*P\s*打包/i)
  const n = m ? parseInt(m[1], 10) : 1
  return n >= 1 ? n : 1
}

// 포장 그룹 하나의 申报단가. rows = [대표행, ...구성품행] 의 원본 행(배열). 가격 칸이 빈 구성품 행은 0 으로 더해진다
export function packUnitPrice(rows, cPrice, cReq) {
  let sum = 0
  for (const raw of rows) sum += numOf(raw && raw[cPrice]) * packCount(raw && raw[cReq])
  return round6(sum)
}

// 직원이 인식 실패 건을 고쳐 올린 파일 판별(2026-10-08 YZY 16:21 「인식이 잘못돼서 다시 표시해 올린 것, 맞으면 손대지 않는다」).
// 실측(10-07 원본 vs 「-10.7」): 고친 합포장 행은 가격USD 를 이미 단가×N 으로 바꾸거나(0.04→0.08, 0.13→0.52), 출고수량을 ×N 으로 바꿨고(50→100),
// 원래 「/」 였던 구성품 행에 수량(100·39·10 …)을 직접 적었다. 값만으로는 원본의 0.5 와 구별되지 않으므로 확실한 표지 하나만 믿는다:
//   같은 박스에 「合并打包」 행이 바코드가 다른 채로 숫자 수량을 둘 이상 가지면 = 구성품 「/」 에 수량을 적어 고친 박스 → 그 박스는 곱하지 않는다(status 'kept').
// 그 밖에는 원본으로 보고 곱한다(status 'applied'). 순수 함수라 같은 파일을 몇 번 올려도 결과가 같다(멱등).
function correctedBoxes(aoa, parsed, cReq) {
  const seen = new Map()
  for (const r of (parsed && parsed.rows) || []) {
    if (r.수량종류 !== 'qty' || !(r.수량 > 0)) continue
    const raw = aoa[r.line - 1] || []
    if (!/合并打包/.test(txt(raw[cReq]))) continue
    if (!seen.has(r.박스번호키)) seen.set(r.박스번호키, new Set())
    seen.get(r.박스번호키).add(r.바코드)
  }
  return new Set([...seen].filter(([, codes]) => codes.size >= 2).map(([k]) => k))
}

// parsed.groups → Map(엑셀 행번호 → { price, status, n }). 수량 행(대표·합산된 행)마다 같은 값을 준다. 그룹이 아닌 행은 map 에 없다(원래 가격 그대로)
// status: 'plain' 일반(바뀐 것 없음) · 'applied' 단가×N(합) 적용 · 'kept' 직원이 고친 박스라 가격 그대로
export function packUnitPrices(aoa, parsed, cPrice, cReq) {
  const out = new Map()
  const fixed = correctedBoxes(aoa, parsed, cReq)
  for (const g of (parsed && parsed.groups) || []) {
    if (!g.대표행) continue
    const get = r => aoa[r.line - 1] || []
    const head = get(g.대표행)
    const rows = [head].concat((g.구성품행 || []).map(get))
    const n = packCount(head[cReq])
    let v
    if (/合并打包/.test(txt(head[cReq])) && fixed.has(g.대표행.박스번호키)) v = { price: numOf(head[cPrice]), status: 'kept', n }
    else {
      const price = packUnitPrice(rows, cPrice, cReq)
      v = { price, status: price === numOf(head[cPrice]) ? 'plain' : 'applied', n }
    }
    out.set(g.대표행.line, v)
    ;(g.합산행 || []).forEach(l => out.set(l, v))
  }
  return out
}

// 散货 박스 毛重 소수점 제거(2026-10-08 YZY) — 소수를 버린 정수, 0 보다 크면 최소 1(0 은 「모름·빈칸」이라서). 托盘은 palletKg(십 단위 내림)
export function kgInt(kg) {
  const v = Math.round(numOf(kg) * 1e6) / 1e6
  if (!(v > 0)) return 0
  return Math.max(1, Math.floor(v))
}

// ───────────────────────── 고정 申报金额(2026-10-11 YZY) ─────────────────────────
// 韩文标签资料 에 적어 둔 상품별 고정 申报单价(USD/件)를 箱单发票 申报单价 와 出库资料文件 价格USD 가 같이 쓰는 한 함수.
// 고정금액이 있으면 그 값이 우선이고 합포장 ×N 도 하지 않는다. 없으면 base(= 파일 단가 또는 합포장 계산값) 그대로 — 기존 동작과 같다.
// fixedMap 의 열쇠 = 「화주|바코드」(fixedKey). 값이 양수가 아니면(0·음수·문자·NaN) 없는 것으로 본다
export function fixedKey(companyid, barcode) {
  return String(companyid == null ? '' : companyid).trim() + '|' + String(barcode == null ? '' : barcode).replace(/\s+/g, '')
}
// item = { companyid, barcode, base } → { price, fixed }
export function declaredUnitPriceUSD(item, fixedMap) {
  const base = item ? item.base : 0
  if (!item || !fixedMap || !item.barcode) return { price: base, fixed: false }
  const raw = fixedMap instanceof Map ? fixedMap.get(fixedKey(item.companyid, item.barcode)) : fixedMap[fixedKey(item.companyid, item.barcode)]
  const v = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN)
  return v > 0 && isFinite(v) ? { price: round6(v), fixed: true } : { price: base, fixed: false }
}

// 확정 영문 품명(namesMap 의 열쇠 = fixedKey, 2026-10-16 YZY #27) — 비었으면 파일의 영어품명(base) 그대로
export function confirmedNameEn(item, namesMap) {
  const base = item && item.base != null ? item.base : ''
  if (!item || !namesMap || !item.barcode) return base
  const k = fixedKey(item.companyid, item.barcode)
  const v = namesMap instanceof Map ? namesMap.get(k) : namesMap[k]
  const t = typeof v === 'string' ? v.trim() : ''
  return t || base
}
